/** `/join/:inviteToken` — fetches invite info, then lets the joiner pick a name, slot and role. */
import { useEffect, useState, type FormEvent } from 'react';
import { CHARACTER_SLOTS, type CharacterSlotId, type InviteInfo } from '@momentum/shared';
import { ApiError, getInviteInfo, joinSession } from '../api.ts';
import { loadCredentials, saveCredentials } from '../session/credentials.ts';
import { Link, useNavigate } from '../shell/router.tsx';
import { CharacterBadge } from '../components/CharacterBadge.tsx';
import { strings } from '../shell/strings.ts';

export interface JoinSessionProps {
  inviteToken: string;
}

export function JoinSession({ inviteToken }: JoinSessionProps) {
  const navigate = useNavigate();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [characterSlot, setCharacterSlot] = useState<CharacterSlotId | null>(null);
  const [role, setRole] = useState<'player' | 'spectator'>('player');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getInviteInfo(inviteToken)
      .then((value) => {
        if (cancelled) return;
        if (loadCredentials(value.sessionId)) {
          navigate(`/s/${value.sessionId}`);
          return;
        }
        setInfo(value);
        setRole(value.canJoinAsPlayer ? 'player' : 'spectator');
        setCharacterSlot(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : strings.join.invalidInvite);
      });
    return () => {
      cancelled = true;
    };
  }, [inviteToken, navigate]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await joinSession(inviteToken, {
        displayName,
        role,
        characterSlot: role === 'player' && characterSlot ? characterSlot : undefined,
      });
      saveCredentials(response.sessionId, response.participant);
      navigate(`/s/${response.sessionId}`);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : strings.common.error);
      if (err instanceof ApiError && err.code === 'slot_taken') {
        try {
          const latest = await getInviteInfo(inviteToken);
          setInfo(latest);
          setRole(latest.canJoinAsPlayer ? 'player' : 'spectator');
          setCharacterSlot((selected) => (selected && latest.availableSlots.includes(selected) ? selected : null));
        } catch {
          // Keep the actionable join error visible if refreshing availability fails.
        }
      }
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="page stack">
        <h1>{strings.join.title}</h1>
        <p className="badge badge-danger">{loadError}</p>
        <footer className="footer">{strings.footer.conceptPrototype}</footer>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="page">
        <p role="status" aria-live="polite">
          {strings.session.loading}
        </p>
      </div>
    );
  }

  if (info.status === 'ended') {
    return (
      <div className="page stack">
        <h1>{strings.join.title}</h1>
        <p>{strings.join.ended}</p>
        <footer className="footer">{strings.footer.conceptPrototype}</footer>
      </div>
    );
  }

  const lateJoinNotice =
    info.status === 'running'
      ? info.lateJoin === 'spectator'
        ? strings.join.lateJoinSpectator
        : info.lateJoin === 'wait'
          ? strings.join.lateJoinWait
          : strings.join.lateJoinReject
      : null;

  const canJoinAtAll = info.canJoinAsPlayer || info.canJoinAsSpectator;

  return (
    <div className="page stack momentum-join">
      <span className="momentum-entry-kicker">Momentum · Together</span>
      <h1>Join your team</h1>
      <p>
        {info.hostName} · {info.players}/{info.maxPlayers} players
      </p>
      {lateJoinNotice ? <p className="badge badge-highlight">{lateJoinNotice}</p> : null}

      {!canJoinAtAll ? (
        <p className="badge badge-danger">{strings.join.lobbyFull}</p>
      ) : (
        <form className="stack" onSubmit={submit}>
          <div className="field">
            <label htmlFor="join-name">{strings.join.displayNameLabel}</label>
            <input
              id="join-name"
              type="text"
              required
              maxLength={32}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={strings.join.displayNamePlaceholder}
            />
          </div>

          {role === 'player' && info.availableSlots.length > 0 ? (
            <details>
              <summary>{strings.join.characterLabel} (optional)</summary>
              <fieldset
                aria-label={strings.join.characterLabel}
                className="stack-sm"
                style={{ border: 'none', padding: 0, margin: 'var(--space-3) 0 0' }}
              >
                <div className="row">
                  <button
                    type="button"
                    className={characterSlot === null ? 'button card-selected' : 'button'}
                    aria-pressed={characterSlot === null}
                    onClick={() => setCharacterSlot(null)}
                  >
                    Surprise me
                  </button>
                  {info.availableSlots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      className={characterSlot === slot ? 'button card-selected' : 'button'}
                      aria-pressed={characterSlot === slot}
                      onClick={() => setCharacterSlot(slot)}
                    >
                      <CharacterBadge slot={slot} displayName={CHARACTER_SLOTS[slot].label} size={32} />
                    </button>
                  ))}
                </div>
              </fieldset>
            </details>
          ) : null}

          {submitError ? (
            <p className="badge badge-danger" role="alert">
              {submitError}
            </p>
          ) : null}

          <button
            type="submit"
            className="button button-primary"
            disabled={
              submitting ||
              !displayName.trim() ||
              (role === 'player' && !info.canJoinAsPlayer) ||
              (role === 'spectator' && !info.canJoinAsSpectator)
            }
          >
            {submitting ? strings.join.submitting : strings.join.submit}
          </button>
        </form>
      )}
      <p className="text-muted">Running this session? <Link to="/host">Open host controls</Link>. Guest invitations never grant host access.</p>
      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}
